import type { FetchRouteOptions } from '~/types/fetch'

export const fetchRoute = async (
  route: string,
  method: string = 'GET',
  options: FetchRouteOptions = {}
) => {
  let data = null
  const { body, headers, parseErrorResponse, parseSuccessResponse } =
    options ?? {}

  try {
    const response = await fetch(route, {
      method,
      ...(body
        ? {
            body,
            headers: headers ?? {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            }
          }
        : {
            ...headers
          })
    })
    if ([200, 204].includes(response.status)) {
      if (parseSuccessResponse) {
        data = await response.json()
      }
      return { data, status: response.status, isSuccess: true }
    } else {
      if (parseErrorResponse) {
        data = await response.json()
      }
      return { data, status: response.status, isSuccess: false }
    }
  } catch (error) {
    console.error(error)
  }
  return { data, status: null, isSuccess: false }
}

export const SESSION_ENDED_MESSAGE = 'Your session has ended. Redirecting…'
export const CONNECT_FAILED_MESSAGE =
  'Could not connect. Please check your details and try again.'

// Submits an integration's connect form and returns either the parsed success
// payload or `{ error }` for the modal to render.
//
// Integration routes deliberately never answer with 401/403 for third-party
// credential failures, because `fetchWithErrorHandling` reads those as the
// organizer's own session expiring and sends them to /sign-in — a rejected
// ActionKit password used to surface as "you have been logged out". So a 401/403
// reaching here really is a session/authorization failure and still redirects;
// everything else is shown inline with the backend's specific message.
export const submitIntegrationConnect = async (
  route: string,
  body: object
): Promise<any> => {
  const { data, status, isSuccess } = await fetchRoute(route, 'PUT', {
    body: JSON.stringify(body),
    parseErrorResponse: true,
    parseSuccessResponse: true
  })

  if (status === 401 || status === 403) {
    redirectRoute(null, status === 401 ? '/sign-in' : '/')
    return { error: SESSION_ENDED_MESSAGE }
  }

  if (!isSuccess) {
    return { error: data?.error ?? CONNECT_FAILED_MESSAGE }
  }

  return data
}

export const sortOrderMap = {
  ascending: 'ASC',
  descending: 'DESC'
}

export const getPagingUri = (
  offset: number,
  limit: number,
  search: any,
  orderBy: string,
  optionalQueryParams: string = null
) => {
  let paging = `?offset=${offset}&limit=${limit}&orderBy=${encodeURIComponent(
    orderBy
  )}`
  if (search && search.trim() !== '') {
    paging = paging + '&search=' + search
  }
  if (optionalQueryParams) {
    paging = paging + optionalQueryParams
  }

  return paging
}

export async function fetchIgnoreErrors(
  url: string,
  options: RequestInit = {}
) {
  options.credentials = 'include'
  try {
    let response = await tryFetchIgnoreErrors(url, options)
    return response
  } catch (err) {
    let response = await tryFetchIgnoreErrors(url, options)
    return response
  }
}

export type FetchResult = { data: any; error: string | null }

// fetchIgnoreErrors collapses every failure to null, which leaves a caller
// unable to tell "the server returned nothing" from "the request failed" -- a
// widget rendering an empty state cannot then avoid claiming there is no data
// when really it never got an answer. This keeps the same shape and the same
// single retry -- except on abort, below -- but hands back the backend's error
// message. It keeps fetchWithErrorHandling's 401/403 session handling too; see
// tryFetchWithResult for why that has to live here rather than in the caller.
export async function fetchWithResult(
  url: string,
  options: RequestInit = {}
): Promise<FetchResult> {
  options.credentials = 'include'
  try {
    return await tryFetchWithResult(url, options)
  } catch {
    // An abort is the one throw not worth retrying: the caller has already
    // decided it does not want this response, so re-issuing the request does
    // the opposite of what the retry is for. Callers that abort discard the
    // return value, so this shape only matters if one ever stops doing that --
    // it is the terminal-failure shape rather than {data: null, error: null},
    // which a caller would read as a successful empty answer.
    if (options.signal?.aborted) {
      return { data: null, error: CONNECT_FAILED_MESSAGE }
    }

    // Transport failure (DNS, offline, dropped connection). tryFetchWithResult
    // only throws for those -- an HTTP error status comes back as a value -- so
    // retrying once is worthwhile rather than pointless.
    try {
      return await tryFetchWithResult(url, options)
    } catch {
      return { data: null, error: CONNECT_FAILED_MESSAGE }
    }
  }
}

async function tryFetchWithResult(
  url: string,
  options: RequestInit
): Promise<FetchResult> {
  const response = await fetch(url, options)
  // A non-JSON error body (an HTML error page, an empty 502) must not throw
  // past the caller as a transport failure -- it is a real answer, just an
  // unusable one, and retrying it is pointless.
  let parseFailed = false
  const body = await response.json().catch(() => {
    parseFailed = true
    return null
  })

  // An ok response whose body did not parse is not a successful empty answer.
  // The common cause is a connection dropped after headers but during the body:
  // fetch has already resolved ok, so only the json() rejection reveals it.
  // Returning {data: null, error: null} here would hand the caller the one shape
  // this helper exists to prevent -- the same shape the abort branch in
  // fetchWithResult deliberately avoids -- and the caller would render "no data"
  // for a request that never delivered an answer.
  if (response.ok) {
    return parseFailed
      ? { data: null, error: CONNECT_FAILED_MESSAGE }
      : { data: body, error: null }
  }

  // Surfacing the backend's message is the whole point of this helper, and the
  // cost of opting out of fetchWithErrorHandling is that session handling has to
  // happen here instead -- the same trade saveNotificationEmails documents in
  // rippleClient.ts. isAuthenticated answers 401 with `{message: 'Not logged
  // in'}` and isSecure answers 403 with `{requires2FA: true}`; neither carries an
  // `error` key, so without this an expired session renders as "Request failed
  // (401)" in whatever empty state the caller draws, on a page that is already
  // dead.
  //
  // 403 is gated on there being nothing to show: a route that answers 403 with
  // its own `error` means it for the user to read inline, and signing them out
  // over it would be wrong.
  if (response.status === 401 || (response.status === 403 && !body?.error)) {
    redirectRoute(null, response.status === 401 ? '/sign-in' : '/')
    return { data: null, error: SESSION_ENDED_MESSAGE }
  }

  return {
    data: null,
    error: body?.error ?? `Request failed (${response.status})`
  }
}

export async function fetchWithErrorHandling(
  url: string,
  options: RequestInit = {}
) {
  options.credentials = 'include'
  try {
    let response = await tryFetch(url, options)
    return response
  } catch (err) {
    let response = await tryFetch(url, options)
    return response
  }
}

async function tryFetch(url, options) {
  let response = await fetch(url, options)

  if (response.status === 401) {
    redirectRoute(null, '/sign-in')
    return false
  }

  if (response.status === 403) {
    redirectRoute(null, '/')
    return false
  }

  if (!response.ok) {
    throw new Error(`${response?.status} returned from ${url}`)
  }

  const data = await response.json()
  return data
}

async function tryFetchIgnoreErrors(url, options) {
  let response = await fetch(url, options)

  if (!response.ok) {
    return null
  }

  const data = await response.json()
  return data
}
export const redirectRoute = (res, url: string) => {
  if (res) {
    res.writeHead(302, { Location: url })
    return res.end()
  } else {
    if (
      globalThis.window !== undefined &&
      globalThis.location.pathname !== url
    ) {
      globalThis.location.href = url
    }
  }
}
